// client/src/utils/notify.js
//
// Utilidades compartidas para avisar al usuario de eventos en tiempo real:
//  - playChime(): un sonido corto (sintetizado con WebAudio, sin archivo de audio).
//  - showNotification(): una notificación del navegador/sistema (móvil o escritorio)
//    si el usuario dio permiso.
//  - alertIncoming(): atajo = sonido + notificación.
//
// Las usan el panel del agente, el widget del Canal Web y las notificaciones del
// administrador. Todo va envuelto en try/catch porque estas APIs no existen en todos
// los navegadores y, dentro de un iframe de otro dominio, pueden estar restringidas.

let audioCtx = null;

// El navegador bloquea el audio hasta que hay un gesto del usuario (política de
// autoplay). Llamar a esto en el primer click/tecla/envío deja el AudioContext listo.
export function unlockAudio() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!audioCtx) audioCtx = new Ctx();
    if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {});
  } catch { /* noop */ }
}

// Pide permiso para mostrar notificaciones (idempotente). Conviene llamarlo tras un
// gesto del usuario; si ya se concedió o denegó, no hace nada.
export function requestNotificationPermission() {
  try {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  } catch { /* noop */ }
}

// Reproduce un "ding-dong" corto y agradable con dos tonos.
export function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!audioCtx) audioCtx = new Ctx();
    if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {});
    const ctx = audioCtx;
    const now = ctx.currentTime;
    [[880, 0], [1174.66, 0.13]].forEach(([freq, offset]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + offset);
      gain.gain.exponentialRampToValueAtTime(0.22, now + offset + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.25);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + offset);
      osc.stop(now + offset + 0.26);
    });
  } catch { /* noop */ }
}

// ¿La pestaña está en segundo plano (oculta o sin foco)? Se usa para no molestar con
// una notificación del sistema cuando el usuario ya está mirando la pantalla.
function isBackground() {
  try {
    if (document.hidden) return true;
    if (typeof document.hasFocus === 'function') return !document.hasFocus();
    return false;
  } catch {
    return true;
  }
}

// Muestra una notificación del navegador si hay permiso. Por defecto solo la muestra
// cuando la pestaña está en segundo plano (requireFocusAway); pásale false para
// mostrarla siempre (eventos importantes como un pedido/cita nueva).
export function showNotification(title, { body, tag, icon = '/favicon.ico', requireFocusAway = true } = {}) {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    if (requireFocusAway && !isBackground()) return;
    const n = new Notification(title, { body, tag, icon, renotify: !!tag });
    n.onclick = () => { try { window.focus(); n.close(); } catch { /* noop */ } };
  } catch { /* noop */ }
}

// Sonido + notificación, para mensajes entrantes de chat.
export function alertIncoming(title, body, { tag } = {}) {
  playChime();
  showNotification(title, { body, tag });
}
